/**
 * API смарт-контракта инициализируется следующим образом: подключенный на сайте модуль SmartContract сам инъектирует
 * бандлы. Когда смарт-контракт подключился, он рапортует о готовности начать инициализацию событием 'kodix:smartContract:bundleReady'
 * Увидев это событие мы можем создать скрытый инпут, в котором будут храниться данные о машине, которые заберет смарт-контракт
 * После того, как смарт-контракт инициализаруется, он пришлёт объект с данными и с методами АПИ в событии kodix:smartContract:booted
 * Затем мы можем использовать эти методы апи для управления смарт-контрактом и подписаться на событие kodix:smartContract:updated
 * для подписки на изменения данных смарт-контракта
 */

import { useStore } from 'effector-react'
import { CarData } from 'features/car/types'
import { encodeCarData } from 'lib/encode-car-data'
import { logger } from 'lib/logger'
import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  $dealerships,
  $loading,
  $replaceForms,
  $state,
  setDealerships,
  setLoading,
  setReplaceForms,
  setState
} from '../model'
import { DealerShipToWidgetMap } from '../types'
import { SmartContractSingleCarInitEvent, SmartContractSingleCarMethods, SmartContractSingleCarState } from './types'

const emptySmartContractApi = {
  setActiveWidgetId: () => {},
  openWidget: () => {},
  showTradeIn: () => {},
  enableTradeIn: () => {},
  disableTradeIn: () => {},
  showCredit: () => {},
  enableCredit: () => {},
  disableCredit: () => {},
  enableBenefit: () => {},
  disableBenefit: () => {},
  changePresonalData: () => {},
  createSmartContract: () => {},
  setAnalyticsTags: () => {},
  pushAnalyticsTags: () => {},
  showOnlinePayment: () => {}
}

export type SmartContractAPI = {
  state: SmartContractSingleCarState | null
  api: SmartContractSingleCarMethods
  init: (carData: CarData) => void
  touched: boolean
  loading: boolean
  activeWidgetId: string | null
  dealerShipToWidgetIdMap: DealerShipToWidgetMap | null
  failed: boolean
  formsWillBeReplaced: boolean
}

let methods: SmartContractSingleCarMethods = emptySmartContractApi
let initRequested = false

export const useSingleCarSmartContractAPI = (): SmartContractAPI => {
  const state = useStore($state)
  const [failed, setFailed] = useState(false)
  const dealerships = useStore($dealerships)
  const loading = useStore($loading)
  const replaceForms = useStore($replaceForms)

  const init = useCallback((carData: CarData) => {
    if (!initRequested) {
      initRequested = true

      document.addEventListener('kodix:onePlatform:smartContractOptions', ((
        e: CustomEvent<{ dealershipToWidgetMap: DealerShipToWidgetMap; replace_car_order_form: boolean }>
      ) => {
        logger.log('[SC/kodix:onePlatform:smartContractOptions]', e.detail)
        setDealerships(e.detail.dealershipToWidgetMap)
        setReplaceForms(
          e.detail.replace_car_order_form &&
            carData.dealers.findIndex(({ id }) => Object.keys(e.detail.dealershipToWidgetMap).includes(id)) !== -1
        )
      }) as EventListener)

      document.addEventListener('kodix:smartContract:booted', ((e: CustomEvent<SmartContractSingleCarInitEvent>) => {
        logger.log('[SC/kodix:smartContract:booted]', e.detail)
        const data = e.detail.api.v1.data
        methods = e.detail.api.v1.methods
        setState(data)
      }) as EventListener)

      document.addEventListener('kodix:onePlatform:smartContractBootFailed', (() => {
        logger.error('[SC/kodix:onePlatform:smartContractBootFailed] Falling back to non-SC mode')
        setFailed(true)
      }) as EventListener)

      document.addEventListener('kodix:smartContract:updated', ((e: CustomEvent<SmartContractSingleCarInitEvent>) => {
        logger.log('[SC/kodix:smartContract:updated]', e.detail)
        const data = e.detail.api.v1.data
        logger.log('[SC/DataUpdated]', data)
        setState(data)
      }) as EventListener)

      createInputWithData(carData)

      document.dispatchEvent(new CustomEvent('kodix:ecom:isReadyForSmartContract'))
    }
  }, [])

  const touched = useMemo(() => {
    if (!state) return false
    return (
      state.benefits.selected.length > 0 ||
      state.tradeIn.behaviourByUser === 'applied' ||
      state.credit.behaviourByUser === 'credit_applied'
    )
  }, [state])

  return {
    init,
    state,
    api: methods,
    dealerShipToWidgetIdMap: dealerships,
    touched,
    loading: !state?.settings.isWidgetLoaded,
    failed,
    activeWidgetId: state?.settings.activeDealership.widgetId ? state.settings.activeDealership.widgetId : null,
    formsWillBeReplaced: replaceForms
  }
}

export const determineWidgetId = (
  productType: CarData['productType'],
  dealerId: string,
  dealerToWidgetId: DealerShipToWidgetMap
) => {
  const possibleIds = dealerToWidgetId[dealerId]
  if (!possibleIds) {
    logger.log(`[SC/determineWidgetId]', 'Can't determine widget id!`, { productType, dealerId, dealerToWidgetId })
    return
  }
  if (productType === 'new' && !!possibleIds.new) return possibleIds.new
  if (productType === 'used' && !!possibleIds.used) return possibleIds.used
  return possibleIds.any
}

const createInputWithData = (carData: CarData) => {
  const config = {
    default_image_url: carData.images[0]?.src || carData.defaultImageUrl,
    vin: carData.vinRaw,
    price_without_discounts: carData.price,
    final_price: carData.priceWithDiscounts,
    model_full_name: carData.title,
    model_name: carData.model.name,
    brand_name: carData.brand.name,
    dealer_code: carData.dealers.length ? carData.dealers[0].code : undefined,
    dealers_code: carData.dealers.length ? carData.dealers.map((item) => item.code) : undefined,
    dealer_id: carData.dealers.length ? carData.dealers[0].id : undefined,
    dealers_id: carData.dealers.length ? carData.dealers.map((item) => item.id) : undefined,
    is_new: carData.productType,
    brand_id: carData.brand.id,
    model_id: carData.model.id,
    restyling_id: carData.restyling.id,
    complectation_id: carData.complectationId,
    marketing_complectation_id: carData.marketingComplectation.id,
    marketing_complectation_code: carData.marketingComplectation.code,
    tradein_discount: carData.tradeInDiscount,
    credit_discount: carData.creditDiscount,
    insurance_discount: carData.insuranceDiscount,
    credit_tradein_discount: carData.creditTradeInDiscount,
    // TODO: после доработок бэка проверить upholstery и engine_power_horse_power
    engine_power_horse_power: carData.enginePowerHorsePower,
    base_color_name: carData.baseColor.name,
    upholstery_name: carData.upholstery.name,
    upholstery_material: carData.upholstery.material,
    // TODO: поменять при работе над мультидилерством
    cars: carData.collapsedCars,
    year: carData.yearRAW,
    mileage: carData.mileage,
    additional_options_cost: carData.additionalEquipmentCost,
    generation_name: carData.generation.name,
    base_color: carData.baseColor,
    status: carData.status,
    wheel_drive: carData.wheelDrive,
    fuel: carData.fuel,
    body: carData.body,
    transmission: carData.transmission
  }

  const inputWithData = document.createElement('input')
  inputWithData.hidden = true
  inputWithData.id = 'car_data_encoded'
  inputWithData.value = encodeCarData(config)
  document.body.append(inputWithData)

  logger.log('[SC/InputInjected]', config)
}
